<template>
  <div>
  <nav class="navbar-wrapper">
    <!-- Maintenance Mode Banner -->
    <div v-if="maintenanceActive" class="maintenance-banner">
      <v-icon size="small" class="mr-1">mdi-alert</v-icon>
      <strong>{{ t.navbar ? t.navbar.maintenance_active : 'MAINTENANCE MODE ACTIVE!' }}</strong>
      <span class="ml-2">{{ t.navbar ? t.navbar.maintenance_reminder : "Don't forget to deactivate it!" }}</span>
      <v-btn size="x-small" variant="flat" color="white" class="ml-3 maintenance-off-btn" @click="cancelMaintenance" :loading="cancellingMaintenance">
        <v-icon size="small" class="mr-1">mdi-power</v-icon>
        {{ t.navbar ? t.navbar.deactivate : 'Deactivate' }}
      </v-btn>
    </div>
    <!-- Impersonation Banner -->
    <div v-if="isImpersonated" class="impersonation-banner">
      <v-icon size="small" class="mr-1">mdi-account-switch</v-icon>
      <span>{{ t.navbar ? t.navbar.impersonated_by : 'Impersonated by: ' }}<strong>{{ impersonatedByName }}</strong></span>
      <v-btn size="x-small" variant="text" class="ml-2" @click="endImpersonation">
        <v-icon size="small" class="mr-1">mdi-close</v-icon>
        {{ t.navbar ? t.navbar.close_tab : 'Close tab' }}
      </v-btn>
    </div>
    <!-- Top Header Bar -->
    <div class="navbar-header">
      <div class="navbar-header-content">
        <!-- Left: Hamburger Menu -->
        <v-btn icon variant="text" @click="drawer = !drawer" size="small" class="mr-2">
          <v-icon>mdi-menu</v-icon>
        </v-btn>

        <!-- Logo (from distributor) - clickable to dashboard -->
        <router-link to="/" class="navbar-logo" v-if="distributorLogo || distributorBrand || userLoaded">
          <img v-if="distributorLogo" :src="distributorLogo" alt="Logo" class="logo-img" />
          <span class="logo-text">{{ distributorBrand || user?.client?.name || 'Veridian' }}</span>
        </router-link>

        <!-- Breadcrumb: Client Path -->
        <div class="navbar-breadcrumb" v-if="ancestorPath && ancestorPath.length > 0">
          <template v-for="(item, index) in ancestorPath" :key="item.id">
            <span class="breadcrumb-separator" v-if="index > 0">›</span>
            <span
              class="breadcrumb-item"
              :class="{ 'breadcrumb-current': index === ancestorPath.length - 1 }"
            >
              {{ item.name }}
            </span>
          </template>
        </div>

        <v-spacer></v-spacer>

        <!-- Right side: Settings, User Info, Logout, Language -->
        <div class="navbar-header-right">
          <!-- Settings -->
          <v-btn icon variant="text" size="small" :to="'/settings'" class="mr-1">
            <v-icon size="small">mdi-cog</v-icon>
          </v-btn>

          <!-- User Info (desktop: show name/email + icon, mobile: just icon) -->
          <div class="user-info d-none d-md-flex align-center mr-2 user-info-link" v-if="userLoaded" @click="updateProfile()">
            <div class="user-details text-right mr-2">
              <div class="user-name text-body-2 font-weight-medium">{{ user?.username || user?.name }}</div>
              <div class="user-email text-caption text-grey">{{ user?.email }}</div>
            </div>
          </div>
          <!-- User Profile Icon (always visible) -->
          <v-avatar
            v-if="userLoaded"
            size="32"
            @click="updateProfile()"
            class="mr-1 cursor-pointer"
            :color="user?.avatar ? undefined : 'white'"
          >
            <v-img v-if="user?.avatar" :src="user.avatar" cover />
            <v-icon v-else size="24" color="primary">mdi-account-circle</v-icon>
          </v-avatar>

          <!-- Notifications -->
          <NotificationsPanel v-if="userLoaded" />

          <!-- Language Selector -->
          <LanguageSelector size="x-small" />

          <!-- Logout (last on right) -->
          <v-btn icon variant="text" size="small" color="error" @click="confirmLogout" class="ml-1 logout-btn">
            <v-icon size="24" color="error">mdi-logout</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Main toolbar with sections -->
    <div class="navbar-toolbar">
      <div class="navbar-content">
        <!-- Left side sections -->
        <div class="navbar-sections">

          <!-- Administrative -->
          <div class="nav-section bg-blue px-2 py-1 mx-0">
            <div class="section-title text-white font-weight-bold">{{ t.menu.administrative }}</div>
            <div class="section-buttons d-flex ga-2">
              <v-btn size="small" variant="text" class="text-white" :to="'/clients'">{{ t.menu.clients }}</v-btn>
              <v-btn size="small" variant="text" class="text-white" :to="'/users'">{{ t.menu.users }}</v-btn>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn size="small" variant="text" class="text-white" v-bind="props">
                    {{ t.menu.groups || 'Groups' }}
                    <v-icon size="small" class="ml-1">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item :to="'/imsi-groups'">
                    <v-list-item-title>{{ t.menu.imsi_groups || 'IMSI Groups' }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item :to="'/msisdn-groups'">
                    <v-list-item-title>{{ t.menu.msisdn_groups || 'MSISDN Groups' }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn size="small" variant="text" class="text-white" v-bind="props">
                    <v-icon size="small" class="mr-1">mdi-dots-horizontal</v-icon>
                    Extra
                    <v-icon size="small" class="ml-1">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item :to="'/commands'">
                    <v-list-item-title>Comenzi Provizionare</v-list-item-title>
                  </v-list-item>
                  <v-list-item :to="'/comments'">
                    <v-list-item-title>{{ t.menu.comments || 'Comments' }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item :to="'/history'">
                    <v-list-item-title>{{ t.menu.history || 'History' }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item :to="'/portability-requests'">
                    <v-list-item-title>{{ t.menu.portability || 'Portabilitate' }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>

          <!-- Client Services -->
          <div class="nav-section bg-purple px-2 py-1 mx-0">
            <div class="section-title text-white font-weight-bold">{{ t.menu.client_services }}</div>
            <div class="section-buttons d-flex ga-2">
              <v-btn size="small" variant="text" class="text-white" :to="'/clientServices'">
                <v-icon size="small" class="mr-1">mdi-magnify</v-icon>
                {{ t.common.search }}
              </v-btn>
              <v-btn size="small" variant="text" class="text-white" @click="showAddClientService">
                <v-icon size="small" class="mr-1">mdi-plus</v-icon>
                {{ t.common.add }}
              </v-btn>
            </div>
          </div>

          <!-- Resources -->
          <div class="nav-section bg-teal px-2 py-1 mx-0">
            <div class="section-title text-white font-weight-bold">{{ t.menu.resources }}</div>
            <div class="section-buttons d-flex ga-2">
              <v-btn size="small" variant="text" class="text-white" :to="'/imsis'">IMSI</v-btn>
              <v-btn size="small" variant="text" class="text-white" :to="'/msisdns'">MSISDN</v-btn>
              <v-btn size="small" variant="text" class="text-white" :to="'/sims'">SIM</v-btn>
              <v-btn size="small" variant="text" class="text-white" :to="'/products'">{{ t.menu.products || 'Products' }}</v-btn>
              <v-btn size="small" variant="text" class="text-white" :to="'/services'">{{ t.menu.services }}</v-btn>
            </div>
          </div>

        <!-- Financial -->
        <div class="nav-section bg-orange px-2 py-1 mx-0">
          <div class="section-title text-white font-weight-bold">{{ t.menu.financial }}</div>
          <div class="section-buttons d-flex ga-2">
            <v-btn size="small" variant="text" class="text-white" :to="'/orders'">{{ t.menu.orders }}</v-btn>
            <v-btn size="small" variant="text" class="text-white" :to="'/reports'">{{ t.menu.reports }}</v-btn>
            <v-btn size="small" variant="text" class="text-white" :to="'/reports/traffic'">
              <v-icon size="small" class="mr-1">mdi-chart-timeline-variant</v-icon>
              Trafic
            </v-btn>
            <v-btn size="small" variant="text" class="text-white" :to="'/commissions'">{{ t.menu.commissions || 'Comisioane' }}</v-btn>
            <v-btn v-if="canDoReferrals" size="small" variant="text" class="text-white" :to="'/referrals'">{{ t.menu.referrals || 'Referrals' }}</v-btn>
            <v-btn v-if="canAccessDunning" size="small" variant="text" class="text-white" :to="'/dunning-rules'">Dunning</v-btn>
            <v-btn v-if="canAccessDunning" size="small" variant="text" class="text-white" :to="'/traffic-alerts'">Alerte Trafic</v-btn>
          </div>
        </div>
        </div>
      </div>
    </div>
  </nav>

    <!-- Logout Confirmation Dialog -->
    <v-dialog v-model="logoutDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">{{ t.menu.sign_out }}</v-card-title>
        <v-card-text>{{ t.messages.confirm_action }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="logoutDialog = false">{{ t.common.cancel }}</v-btn>
          <v-btn color="error" variant="flat" @click="doLogout">{{ t.menu.sign_out }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- My Account Dialog -->
    <v-dialog v-model="showMyAccount" max-width="500">
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center">
          <v-icon class="mr-2">mdi-account-circle</v-icon>
          {{ t.menu.my_account }}
        </v-card-title>
        <v-card-text class="pa-4" v-if="userLoaded">
          <!-- User Section -->
          <div class="mb-4">
            <div class="text-subtitle-1 font-weight-bold mb-2 d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-account</v-icon>
              {{ t.users.user_details }}
            </div>
            <v-table density="compact">
              <tbody>
                <tr>
                  <td class="font-weight-medium" width="40%">{{ t.common.name }}</td>
                  <td>{{ user?.name || '-' }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ t.common.username }}</td>
                  <td>{{ user?.username || '-' }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ t.common.email }}</td>
                  <td>{{ user?.email || '-' }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ t.common.phone }}</td>
                  <td>{{ user?.phone || '-' }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ t.common.status }}</td>
                  <td>
                    <v-chip size="small" :color="user?.user_status_id === 1 ? 'success' : 'error'">
                      {{ user?.user_status_id === 1 ? t.common.active : t.common.inactive }}
                    </v-chip>
                  </td>
                </tr>
                <tr v-if="user?.roles && user.roles.length">
                  <td class="font-weight-medium">{{ t.users.roles }}</td>
                  <td>
                    <v-chip v-for="role in user.roles" :key="role" size="x-small" class="mr-1" color="info">
                      {{ role }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <v-divider class="my-3"></v-divider>

          <!-- Client Section -->
          <div>
            <div class="text-subtitle-1 font-weight-bold mb-2 d-flex align-center">
              <v-icon class="mr-2" color="teal">mdi-domain</v-icon>
              {{ t.users.client }}
            </div>
            <v-table density="compact">
              <tbody>
                <tr>
                  <td class="font-weight-medium" width="40%">{{ t.common.name }}</td>
                  <td>{{ user?.client_name || user?.client?.name || '-' }}</td>
                </tr>
                <tr v-if="user?.client?.short_name">
                  <td class="font-weight-medium">{{ t.clients.short_name }}</td>
                  <td>{{ user.client.short_name }}</td>
                </tr>
                <tr v-if="user?.client?.client_type_name">
                  <td class="font-weight-medium">{{ t.clients.client_type }}</td>
                  <td>{{ user.client.client_type_name }}</td>
                </tr>
                <tr v-if="user?.client?.billing_type">
                  <td class="font-weight-medium">{{ t.clients.billing_type }}</td>
                  <td>
                    <v-chip size="small" :color="user.client.billing_type === 'prepaid' ? 'warning' : 'info'">
                      {{ user.client.billing_type === 'prepaid' ? t.clients.prepaid : t.clients.postpaid }}
                    </v-chip>
                  </td>
                </tr>
                <tr v-if="user?.client?.parent_name">
                  <td class="font-weight-medium">{{ t.clients.parent }}</td>
                  <td>{{ user.client.parent_name }}</td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="flat" @click="showMyAccount = false">{{ t.common.close }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Teleport to="body">
      <div v-if="drawer" class="custom-drawer-backdrop" @click="drawer = false"></div>
      <div class="custom-drawer" :class="{ 'custom-drawer-open': drawer }">
        <v-row class="mt-5" col="12">
         <v-col>
           <v-avatar size="100" @click="updateProfile()" :color="user?.avatar ? undefined : 'white'">
             <v-img v-if="user?.avatar" :src="user.avatar" cover />
             <v-icon v-else size="60" color="primary">mdi-account-circle</v-icon>
           </v-avatar>
           <div v-if="userLoaded && user" class="white--text  mt-2">
             {{ user.email }}
             <div v-if="user.user_status_id==1" class="white--text  mt-2">
               <span class="py-2 px-5 font-weight-bold" style="color:green; background-color: black">Active</span>
             </div>
              <div v-if="user.user_status_id==2" class="white--text  mt-2">
                <span class="py-2 px-5 font-weight-bold" style="color:red; background-color: black">Inactive</span>
              </div>
            </div>
           <p v-else class="white--text subheading mt-1">Necunoscut</p>
           <v-btn color="error" variant="flat" size="small" class="mt-3" @click="confirmLogout(); drawer = false">
             <v-icon start>mdi-logout</v-icon>
             {{ t.menu.sign_out || 'Logout' }}
           </v-btn>
         </v-col>
        </v-row>
      <div class="top4"></div>
      <v-list density="compact">
        <!-- Client Services -->
        <v-list-subheader class="text-white font-weight-bold bg-purple mx-2 rounded">{{ t.menu.client_services }}</v-list-subheader>
        <v-list-item router to="/clientServices" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-magnify</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.common.search }}</v-list-item-title>
        </v-list-item>
        <v-list-item class="ml-2" @click="showAddClientService(); drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-plus</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.common.add }}</v-list-item-title>
        </v-list-item>

        <!-- Administrative -->
        <v-list-subheader class="text-white font-weight-bold bg-blue mx-2 rounded mt-2">{{ t.menu.administrative }}</v-list-subheader>
        <v-list-item router to="/clients" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-folder-account</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.clients }}</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/users" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-account-group</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.users }}</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/imsi-groups" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-folder-multiple</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.imsi_groups || 'IMSI Groups' }}</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/msisdn-groups" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-folder-multiple-outline</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.msisdn_groups || 'MSISDN Groups' }}</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/commands" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-console</v-icon>
          </template>
          <v-list-item-title class="text-black">Comenzi Provizionare</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/comments" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-comment-text-multiple</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.comments || 'Comments' }}</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/history" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-history</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.history || 'History' }}</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/portability-requests" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-swap-horizontal</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.portability || 'Portabilitate' }}</v-list-item-title>
        </v-list-item>

        <!-- Resources -->
        <v-list-subheader class="text-white font-weight-bold bg-teal mx-2 rounded mt-2">{{ t.menu.resources }}</v-list-subheader>
        <v-list-item router to="/imsis" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-numeric</v-icon>
          </template>
          <v-list-item-title class="text-black">IMSI</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/msisdns" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-cellphone</v-icon>
          </template>
          <v-list-item-title class="text-black">MSISDN</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/sims" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-sim</v-icon>
          </template>
          <v-list-item-title class="text-black">SIM</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/products" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-package-variant</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.products || 'Products' }}</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/services" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-cogs</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.services }}</v-list-item-title>
        </v-list-item>

        <!-- My Account -->
        <v-list-subheader class="text-white font-weight-bold bg-green mx-2 rounded mt-2">{{ t.menu.my_account || 'Contul Meu' }}</v-list-subheader>
        <v-list-item router to="/my-msisdns" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-phone-plus</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.my_numbers || 'Numerele Mele' }}</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/my-profile" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-account-circle</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.my_profile || 'Profilul Meu' }}</v-list-item-title>
        </v-list-item>

        <!-- Financial -->
        <v-list-subheader class="text-white font-weight-bold bg-orange mx-2 rounded mt-2">{{ t.menu.financial }}</v-list-subheader>
        <v-list-item router to="/orders" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-cart</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.orders }}</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/reports" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-chart-bar</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.reports }}</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/reports/traffic" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-chart-timeline-variant</v-icon>
          </template>
          <v-list-item-title class="text-black">Trafic</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/commissions" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-cash-multiple</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.commissions || 'Comisioane' }}</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="canDoReferrals" router to="/referrals" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-account-multiple-plus</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.referrals || 'Referrals' }}</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="canAccessDunning" router to="/dunning-rules" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-bell-alert</v-icon>
          </template>
          <v-list-item-title class="text-black">Dunning Rules</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="canAccessDunning" router to="/traffic-alerts" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-shield-alert</v-icon>
          </template>
          <v-list-item-title class="text-black">Alerte Trafic</v-list-item-title>
        </v-list-item>

        <!-- Documentatie -->
        <v-list-subheader class="text-white font-weight-bold bg-deep-purple mx-2 rounded mt-2">Documentatie</v-list-subheader>
        <v-list-item router to="/manual" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-book-open-page-variant</v-icon>
          </template>
          <v-list-item-title class="text-black">Manual</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/howto" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-book-open-variant</v-icon>
          </template>
          <v-list-item-title class="text-black">Guides</v-list-item-title>
        </v-list-item>

        <!-- Legal -->
        <v-list-subheader class="text-white font-weight-bold bg-brown mx-2 rounded mt-2">Legal</v-list-subheader>
        <v-list-item href="/TermsAndConditions.pdf" target="_blank" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-file-document-outline</v-icon>
          </template>
          <v-list-item-title class="text-black">Termeni si Conditii</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/meta_policy" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-shield-lock-outline</v-icon>
          </template>
          <v-list-item-title class="text-black">Privacy</v-list-item-title>
        </v-list-item>

        <!-- Settings -->
        <v-list-subheader class="text-white font-weight-bold bg-grey-darken-1 mx-2 rounded mt-2">{{ t.menu.settings }}</v-list-subheader>
        <v-list-item router to="/settings" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-cog</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.menu.settings }}</v-list-item-title>
        </v-list-item>
        <v-list-item router to="/rolesSettings" class="ml-2" @click="drawer = false">
          <template v-slot:prepend>
            <v-icon color="black" size="small">mdi-shield-account</v-icon>
          </template>
          <v-list-item-title class="text-black">{{ t.users?.roles || 'Roles' }}</v-list-item-title>
        </v-list-item>
      </v-list>
      </div>
    </Teleport>

    <!-- Add New Client Service Dialog -->
    <AddNewClientService
      :show="showAddNewClientServiceDialog"
      @close="onAddServiceDialogClose"
      @serviceAdded="onServiceAdded"
    />

  </div>
</template>

<script>

import {log} from "@/composables/useLog";
import store from "@/store";
import axios from "axios";
import {BASE_URL} from "@/composables/url_config";
import LanguageSelector from "@/components/LanguageSelector.vue";
import AddNewClientService from "@/components/AddNewClientService.vue";
import NotificationsPanel from "@/components/NotificationsPanel.vue";
import { t as tRef } from "@/composables/useLanguages";
import { useMaintenanceCheck } from "@/composables/useMaintenanceCheck";
export default {
  components: { LanguageSelector, AddNewClientService, NotificationsPanel },
  data() {
    return {
      maintenanceActive: false,
      cancellingMaintenance: false,
      message:"un mesaj pt snackbar",
      drawer: false,
      loggedIn: null,
      logoutDialog: false,
      showMyAccount: false,
      showAddNewClientServiceDialog: false,
      links: [
        {  text: 'Client Services', textKey: 'client_services', icon: 'search', route: '/clientServices', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'DashBoard', textKey: 'dashboard', icon: 'dashboard', route: '/',  role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'Clients', textKey: 'clients', icon: 'folder', route: '/clients', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'Users', textKey: 'users', icon: 'people', route: '/users', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'Products', textKey: 'products', icon: 'package-variant', route: '/products', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'Services', textKey: 'services', icon: 'folder', route: '/services', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'Commands', textKey: 'commands', icon: 'console', route: '/commands', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'Comments', textKey: 'comments', icon: 'comment-text-multiple', route: '/comments', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'Sims', textKey: 'sims', icon: 'folder', route: '/sims', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'Telephony Sims', textKey: 'telephony_sims', icon: 'sim_card', route: '/prepaidSimCards', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'Imsis', textKey: 'imsis', icon: 'folder', route: '/imsis', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'Msisdn', textKey: 'msisdns', icon: 'folder', route: '/msisdns', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'Prices', textKey: 'prices', icon: 'folder', route: '/prices', role:"admin", showOnTop:false, showOnLeftMenu:false },
        {  text: 'Settings', textKey: 'settings', icon: 'people', route: '/settings', role:"admin", showOnTop:false, showOnLeftMenu:true },
        {  text: 'Reports', textKey: 'reports', icon: 'assessment', route: '/reports', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'Orders', textKey: 'orders', icon: 'shopping_cart', route: '/orders', role:"admin", showOnTop:true, showOnLeftMenu:true },
        {  text: 'MsisdnClassSettings', textKey: 'msisdn_class_settings', icon: 'people', route: '/msisdnClassSettings', role:"admin", showOnTop:false, showOnLeftMenu:false, showOnSettings:true },
        {  text: 'Test', textKey: 'test', icon: 'user', route: '/test', role:"admin", showOnTop:true, showOnLeftMenu:true },
      ],
      snackbar: false
    }
  },
  props: ["pageName"],
  computed:{
    t() {
      return tRef.value;
    },
    user() {
      return store.getters.getUser
    },
    userLoaded() {
      return this.user && this.user.name
    },
    ancestorPath() {
      return this.user?.ancestor_path || []
    },
    distributorLogo() {
      return this.user?.distributor_logo || this.user?.client?.logo_url || null
    },
    distributorBrand() {
      return this.user?.distributor_brand || null
    },
    isAdmin() {
      return this.user?.admin || this.user?.is_admin || false
    },
    canAccessDunning() {
      if (this.isAdmin) return true
      const clientType = this.user?.userClient?.client_type_name || this.user?.client?.client_type_name
      return clientType === 'uniLevel' || clientType === 'multiLevel'
    },
    canDoReferrals() {
      if (!this.user) return false
      if (this.user.admin || this.user.is_admin) return true
      const clientType = this.user.userClient?.client_type_name || this.user.client?.client_type_name
      return clientType !== 'endUser'
    },
    topLinks(){
      return this.links.filter((link)=>{
        return link.showOnTop==true;
      });
    },
    leftLinks(){
      return this.links.filter((link)=>{
        return link.showOnLeftMenu==true;
      });
    },
    // Impersonation computed properties
    isImpersonated() {
      return sessionStorage.getItem('impersonated') === 'true'
    },
    impersonatedByName() {
      const userStr = sessionStorage.getItem('user')
      if (userStr) {
        try {
          const user = JSON.parse(userStr)
          if (user.impersonated_by) {
            return `${user.impersonated_by.user_name} (${user.impersonated_by.client_name})`
          }
        } catch (e) {
          return 'Necunoscut'
        }
      }
      return 'Necunoscut'
    }
  },
  methods: {
    updateProfile(){
      this.drawer = false
      this.$router.push('/my-profile')
    },
    alertNotification(mesaj){
      alert(mesaj)
    },
    showAddClientService(){
      this.showAddNewClientServiceDialog = true;
    },
    onAddServiceDialogClose(){
      this.showAddNewClientServiceDialog = false;
    },
    onServiceAdded(){
      this.showAddNewClientServiceDialog = false;
    },
    confirmLogout(){
      this.logoutDialog = true;
    },
    doLogout(){
        this.logoutDialog = false;
        this.loggedIn=false
        log("facem aici userul null si ne ducem la homeLoggedOut")
        localStorage.removeItem('token')
        sessionStorage.clear() // Clear impersonation session
        store.commit('setLoggedOut')
        this.$router.replace({ path: '/homeLoggedOut' })
    },
    endImpersonation() {
      // Close this tab (impersonation session)
      window.close()
      // If window.close() doesn't work (some browsers block it), show message
      setTimeout(() => {
        alert('Va rugam inchideti manual acest tab.')
      }, 100)
    },
    async cancelMaintenance() {
      this.cancellingMaintenance = true
      try {
        const token = store.getters.getToken
        const headers = { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }
        const result = await axios.post(BASE_URL + '/home/cancel_under_maintenance_api', {}, { headers })
        if (result.data && !JSON.parse(result.data).error) {
          this.maintenanceActive = false
        }
      } catch (e) {
        log("Error cancelling maintenance: " + e)
      }
      this.cancellingMaintenance = false
    }
  },
  async mounted() {
    const { checkMaintenanceStatus } = useMaintenanceCheck()
    const status = await checkMaintenanceStatus()
    this.maintenanceActive = status.underMaintenance
  }
}
</script>

<style scoped>
.navbar-wrapper {
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #f5f5f5;
}

/* Desktop: add 30px top margin */
@media (min-width: 960px) {
  .navbar-wrapper {
    top: 30px;
  }
}

/* Top Header Bar */
.navbar-header {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.6) 0%, rgba(21, 101, 192, 0.6) 100%);
  padding: 6px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.navbar-header-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.navbar-header .v-btn {
  color: white !important;
}

.navbar-logo {
  display: flex;
  align-items: center;
  margin-right: 16px;
  text-decoration: none;
  cursor: pointer;
}

.logo-img {
  height: 32px;
  max-width: 120px;
  object-fit: contain;
}

.logo-text {
  color: white;
  font-weight: bold;
  font-size: 16px;
}

.navbar-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  color: rgba(255,255,255,0.9);
  font-size: 13px;
}

.breadcrumb-separator {
  color: rgba(255,255,255,0.5);
  margin: 0 2px;
}

.breadcrumb-item {
  color: rgba(255,255,255,0.8);
}

.breadcrumb-current {
  color: white;
  font-weight: 600;
}

.navbar-header-right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.user-info {
  color: white;
}

.user-name {
  color: white;
  line-height: 1.2;
}

.user-email {
  color: rgba(255,255,255,0.7) !important;
  line-height: 1.2;
}

.user-info-link {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;
}

.user-info-link:hover {
  background: rgba(255,255,255,0.1);
}

.navbar-toolbar {
  width: 100%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  background: white;
  padding: 4px 8px;
}
.nav-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 4px;
  min-height: 44px;
  flex: 1 1 auto;
  padding: 2px 6px !important;
  margin: 0 2px;
}
.section-title {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.9;
  margin-bottom: 2px;
  text-align: center;
  width: 100%;
}
.section-buttons {
  display: flex;
  justify-content: center;
  width: 100%;
}
.section-buttons .v-btn {
  font-size: 15px !important;
  min-width: auto !important;
  padding: 0 6px !important;
  height: 28px !important;
  letter-spacing: 0 !important;
}
.navbar-content {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
}

.navbar-sections {
  display: contents;
}

.user-logout-box {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  padding: 2px 4px;
}

/* Custom drawer styles */
.custom-drawer-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
}

.custom-drawer {
  position: fixed;
  top: 0;
  left: -300px;
  width: 280px;
  height: 100vh;
  background: #1976d2;
  z-index: 2001;
  overflow-y: auto;
  transition: left 0.3s ease;
  padding: 10px;
}

.custom-drawer-open {
  left: 0;
}

/* Logout button styling */
.logout-btn {
  min-width: 36px !important;
  width: 36px !important;
  height: 36px !important;
}

.logout-btn:hover {
  background: rgba(244, 67, 54, 0.3) !important;
}

/* Maintenance Mode Banner */
.maintenance-banner {
  background: linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%);
  color: white;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  animation: maintenance-pulse 2s ease-in-out infinite;
}

@keyframes maintenance-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

.maintenance-off-btn {
  color: #d32f2f !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  font-size: 11px !important;
}

/* Impersonation Banner */
.impersonation-banner {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  color: white;
  padding: 6px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.impersonation-banner .v-btn {
  color: white !important;
  background: rgba(255,255,255,0.2);
  font-size: 11px !important;
}

.impersonation-banner .v-btn:hover {
  background: rgba(255,255,255,0.3);
}

/* Cursor pointer for clickable avatars */
.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  opacity: 0.85;
  transition: opacity 0.2s;
}
</style>
